/**
 * 钢笔 / 涂抹笔：工作区视口层（空白处）+ 各可见页内容区覆盖层（页内落笔与归属同步）。
 */
import { useMemo } from 'react';
import { useCanvasStore } from '@/components/canvas/store/canvas.store';
import { getViewportWorldBounds } from '@/components/canvas/page/pageBounds';
import { PenToolOverlay, FreehandToolOverlay } from '@/components/canvas/workspace/overlays';
import { getWorkspaceShellSize } from '@/components/canvas/workspace/model/elementInsertTarget';
import { getPageContentWorldRect } from '@/components/canvas/workspace/model/resolveDrawSurface';
import type { Page } from '@/components/canvas/document/publicTypes';

/** 高于工作区元素层 (1000)，低于页内绘制层 */
const WORKSPACE_DRAW_OVERLAY_Z = 1050;
/** 页内绘制层：压过工作区元素与选区 (1100) */
const PAGE_DRAW_OVERLAY_Z_BOOST = 1100;

export interface DrawToolOverlaysProps {
  pages: Page[];
  visiblePageIds: Set<string>;
  zoom: number;
}

export function DrawToolOverlays({
  pages,
  visiblePageIds,
  zoom,
}: DrawToolOverlaysProps) {
  const penToolActive = useCanvasStore((s) => s.penToolActive);
  const freehandToolActive = useCanvasStore((s) => s.freehandToolActive);
  const panX = useCanvasStore((s) => s.panX);
  const panY = useCanvasStore((s) => s.panY);

  const viewport = useMemo(() => {
    const { width, height } = getWorkspaceShellSize();
    return getViewportWorldBounds(width, height, panX, panY, zoom);
  }, [panX, panY, zoom]);

  const visiblePages = useMemo(
    () => pages.filter((p) => visiblePageIds.has(p.id)),
    [pages, visiblePageIds],
  );

  if (!penToolActive && !freehandToolActive) return null;

  return (
    <>
      <div
        className="absolute"
        style={{
          left: viewport.x,
          top: viewport.y,
          width: viewport.width,
          height: viewport.height,
          zIndex: WORKSPACE_DRAW_OVERLAY_Z,
          pointerEvents: penToolActive || freehandToolActive ? 'auto' : 'none',
        }}
      >
        {penToolActive && (
          <PenToolOverlay
            pageWidth={viewport.width}
            pageHeight={viewport.height}
            zoom={zoom}
            insertVia="workspace"
            originX={viewport.x}
            originY={viewport.y}
          />
        )}
        {freehandToolActive && (
          <FreehandToolOverlay
            pageWidth={viewport.width}
            pageHeight={viewport.height}
            zoom={zoom}
            insertVia="workspace"
            originX={viewport.x}
            originY={viewport.y}
          />
        )}
      </div>

      {visiblePages.map((page) => {
        const { left, top, width, height } = getPageContentWorldRect(page, zoom);
        const z = (page.workspaceLayout.zIndex ?? 0) + PAGE_DRAW_OVERLAY_Z_BOOST;
        return (
          <div
            key={`draw-overlay-${page.id}`}
            className="absolute"
            style={{
              left,
              top,
              width,
              height,
              zIndex: z,
              pointerEvents: penToolActive || freehandToolActive ? 'auto' : 'none',
            }}
          >
            {penToolActive && (
              <PenToolOverlay
                pageWidth={width}
                pageHeight={height}
                zoom={zoom}
                insertVia="page"
                pageId={page.id}
                activatePageOnDraw
              />
            )}
            {freehandToolActive && (
              <FreehandToolOverlay
                pageWidth={width}
                pageHeight={height}
                zoom={zoom}
                insertVia="page"
                pageId={page.id}
                activatePageOnDraw
              />
            )}
          </div>
        );
      })}
    </>
  );
}
